/**
 * Minimal JWT decoder — no signature verification (that's the backend's
 * job). We just decode the payload so we can read `role`, `exp`, `sub`,
 * `tenant_id`.
 *
 * NEVER trust this decode for authorization decisions — the backend
 * verifies signatures on every API call. The frontend uses these
 * claims only for UI gating (show/hide nav, redirect bad-role users).
 */

import type { JWTClaims } from '@cardioq/engine-types';

function base64UrlDecode(input: string): string {
  // Convert from base64url to base64 then atob.
  let s = input.replace(/-/g, '+').replace(/_/g, '/');
  while (s.length % 4) s += '=';
  if (typeof atob === 'function') {
    return atob(s);
  }
  // Node fallback for tests
  return Buffer.from(s, 'base64').toString('binary');
}

export function decodeJwt(token: string): JWTClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;
    const payload = base64UrlDecode(parts[1]);
    const decoded = JSON.parse(payload) as JWTClaims;
    if (typeof decoded.exp !== 'number' || typeof decoded.role !== 'string') {
      return null;
    }
    return decoded;
  } catch {
    return null;
  }
}

export function isJwtExpired(claims: JWTClaims | null, leewaySeconds = 30): boolean {
  if (!claims) return true;
  const nowSec = Math.floor(Date.now() / 1000);
  return claims.exp - leewaySeconds <= nowSec;
}
